import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import StatusSelect from '../../Components/StatusSelect';
import Spinner from '../../Components/Spinner';
import ActivityPanel from './Components/ActivityPanel';
import ContactsPanel from './Components/ContactsPanel';
import { sizeLabel } from './companySize';
import { treatmentOption } from './customsTreatments';
import './Components/CustomsTags.css';
import './TraderDetailsPage.css';

const toTitleCase = (value = '') =>
    value.toLowerCase().replace(/\b([a-z])/g, (c) => c.toUpperCase());

const formatMonthId = (monthId) => {
    const year = Math.floor(monthId / 100);
    const month = monthId % 100;
    return new Date(year, month - 1).toLocaleDateString('en-GB', {
        month: 'short',
        year: '2-digit',
    });
};

const groupEntriesByMonthHs2 = (trades, direction, hs4Filter) => {
    const groups = {};
    trades
        .filter((t) => t.trade_type === direction)
        .forEach((t) => {
            const hs2 = t.commodity_details?.Hs2Code || 'N/A';
            const hs4 = String(t.commodity_details?.Hs4Code || 'N/A');
            if (hs4Filter && !hs4.includes(hs4Filter)) return;
            const key = `${t.month_id}_${hs2}`;
            if (!groups[key]) {
                groups[key] = {
                    trade_id: `${t.trader_id}_${t.month_id}_${hs2}_${t.trade_type}`,
                    month_id: t.month_id,
                    hs2_code: hs2,
                    hs4_code: t.commodity_details?.Hs4Code,
                    hs4_description: t.commodity_details?.Hs4Description,
                    entry_count: 1,
                };
            } else {
                groups[key].entry_count += 1;
            }
        });
    return Object.values(groups).sort((a, b) =>
        b.month_id !== a.month_id
            ? b.month_id - a.month_id
            : b.entry_count - a.entry_count,
    );
};

const buildPivot = (trades, direction, hs4Filter) => {
    const rows = {};
    const months = new Set();
    trades
        .filter((t) => t.trade_type === direction)
        .forEach((t) => {
            const hs4 = String(t.commodity_details?.Hs4Code || 'N/A');
            if (hs4Filter && !hs4.includes(hs4Filter)) return;
            months.add(t.month_id);
            if (!rows[hs4]) {
                rows[hs4] = {
                    hs4_code: hs4,
                    hs4_description:
                        t.commodity_details?.Hs4Description || 'No description',
                    months: {},
                };
            }
            rows[hs4].months[t.month_id] =
                (rows[hs4].months[t.month_id] || 0) + 1;
        });
    return {
        rows: Object.values(rows).sort((a, b) => {
            const sumA = Object.values(a.months).reduce((s, n) => s + n, 0);
            const sumB = Object.values(b.months).reduce((s, n) => s + n, 0);
            return sumB - sumA;
        }),
        months: Array.from(months).sort((a, b) => a - b),
    };
};

// 202511 -> 202512 -> 202601 …
const nextMonthId = (id) => (id % 100 === 12 ? (Math.floor(id / 100) + 1) * 100 + 1 : id + 1);

/* One entry per month of the history window, busy or not, so a quiet month
 * shows as a zero bar instead of silently vanishing from the timeline. The
 * window comes from the server; without it (live-API fallback) the range
 * runs from the first to the last month that has any trade. */
const buildMonthlyMax = (trades, window) => {
    const map = {};
    trades.forEach((t) => {
        if (!map[t.month_id]) {
            map[t.month_id] = { Import: {}, Export: {} };
        }
        const dir = t.trade_type;
        if (!map[t.month_id][dir]) return;
        const hs2 = t.commodity_details?.Hs2Code || t.hs2_code || 'N/A';
        map[t.month_id][dir][hs2] = (map[t.month_id][dir][hs2] || 0) + 1;
    });
    const seen = Object.keys(map).map(Number).sort((a, b) => a - b);
    const first = window?.first_month_id || seen[0];
    const last = window?.last_month_id || seen[seen.length - 1];
    if (!first || !last) return [];
    const months = [];
    for (let m = first; m <= last && months.length < 240; m = nextMonthId(m)) months.push(m);
    return months.map((m) => {
        const importVals = Object.values(map[m]?.Import || {});
        const exportVals = Object.values(map[m]?.Export || {});
        return {
            monthId: m,
            label: formatMonthId(m),
            importMax: importVals.length ? Math.max(...importVals) : 0,
            exportMax: exportVals.length ? Math.max(...exportVals) : 0,
        };
    });
};

const Bars = ({ data, valueKey }) => {
    const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
    // No half-way mark when the top is 1 — it would just repeat "1".
    const mid = max >= 2 ? Math.round(max / 2) : '';
    // Highlight the latest month that had any activity (the newest month
    // in the window can be a quiet one).
    let lastActive = -1;
    data.forEach((d, i) => { if ((d[valueKey] || 0) > 0) lastActive = i; });
    return (
        <>
            <div className="bars-area">
                <div className="bars-y" aria-hidden="true">
                    <span>{max}</span>
                    <span>{mid}</span>
                    <span>0</span>
                </div>
                <div className="bars">
                    {data.map((d, i) => {
                        const v = d[valueKey] || 0;
                        const pct = v === 0 ? 0 : Math.max(6, (v / max) * 100);
                        const isLast = i === lastActive;
                        // "bar-zero", not "empty": .empty is the app-wide
                        // no-results box and its 40px padding swelled zero
                        // bars into tall grey blocks and squeezed real bars out.
                        return (
                            <span
                                key={d.monthId}
                                className={`bar ${v === 0 ? 'bar-zero' : isLast ? 'accent' : ''}`}
                                style={{ height: v === 0 ? '2px' : `${pct}%` }}
                                title={`${d.label}: ${v}`}
                            >
                                <span className="v">{v}</span>
                            </span>
                        );
                    })}
                </div>
            </div>
            <div className="bar-labels">
                {data.map((d) => (
                    <span key={d.monthId}>{d.label.split(' ')[0]}</span>
                ))}
            </div>
        </>
    );
};

// Size in a word — the same labels as the search's size filter.
const sizeText = (company) =>
    company.size_band === 'dormant' ? 'Dormant' : sizeLabel(company) || '—';

const SHOW_DIRECTORS = 6;

/* Current directors, fetched separately from the Companies House API (the
 * server caches them a month). Nothing shows until they arrive, or at all if
 * the server says null (no API key, Companies House busy). */
const Directors = ({ traderId }) => {
    const [officers, setOfficers] = useState(null);
    useEffect(() => {
        let live = true;
        setOfficers(null);
        api.get(`traders/${traderId}/directors`)
            .then((data) => live && setOfficers(data))
            .catch(() => {});
        return () => { live = false; };
    }, [traderId]);
    if (!officers?.directors?.length) return null;
    const shown = officers.directors.slice(0, SHOW_DIRECTORS);
    const more = officers.directors.length - shown.length;
    return (
        <p className="ch-card-industry ch-card-directors">
            <span>{shown.length === 1 ? 'Director' : 'Directors'}</span>
            {shown.map((d, i) => (
                <React.Fragment key={`${d.name}-${i}`}>
                    {i > 0 && '; '}
                    {d.name}
                    {(d.role !== 'Director' || d.appointed) && (
                        <small>
                            {' '}({[d.role !== 'Director' && d.role.toLowerCase(), d.appointed && `since ${d.appointed.slice(0, 4)}`].filter(Boolean).join(', ')})
                        </small>
                    )}
                </React.Fragment>
            ))}
            {more > 0 && `; and ${more} more`}
            {' '}
            <a href={officers.url} target="_blank" rel="noreferrer" title="All officers on Companies House">
                All on Companies House ↗
            </a>
        </p>
    );
};

// £12.4m / £850k / £9,200 — the turnover in a glance.
const money = (value, currency) => {
    const symbol = { GBP: '£', EUR: '€', USD: '$' }[currency] || `${currency || ''} `;
    const n = Math.abs(Number(value) || 0);
    const short = n >= 1e9 ? `${(n / 1e9).toFixed(1).replace(/\.0$/, '')}bn`
        : n >= 1e6 ? `${(n / 1e6).toFixed(1).replace(/\.0$/, '')}m`
        : n >= 1e4 ? `${Math.round(n / 1e3)}k`
        : n.toLocaleString('en-GB');
    return `${value < 0 ? '-' : ''}${symbol}${short}`;
};

/* "£12.4m, up 8% on last year (year to 31 Mar 2026)" — from the accounts
 * the company filed (build-company-accounts.js). */
const turnoverText = (accounts) => {
    const yearTo = new Date(accounts.period_end)
        .toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    const pct = accounts.change_pct;
    const change = pct === null || pct === undefined ? ''
        : Math.abs(pct) < 0.5 ? ', about the same as last year'
        : `, ${pct > 0 ? 'up' : 'down'} ${Math.abs(pct) < 10 ? Math.abs(pct).toFixed(1).replace(/\.0$/, '') : Math.round(Math.abs(pct))}% on last year`;
    return { figure: money(accounts.turnover, accounts.currency), change, yearTo, pct };
};

/* What Companies House says about this trader, when we could tie the HMRC
 * record to a company (monthly match: build-company-matches.js). */
const CompaniesHouseCard = ({ company, traderId }) => {
    const status = String(company.status || '');
    const active = /^active$/i.test(status);
    const formed = company.incorporated
        ? new Date(company.incorporated).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
        : null;
    return (
        <section className="ch-card" aria-label="Companies House">
            <span className="kicker">Companies House</span>
            <dl className="ch-card-facts">
                <div>
                    <dt>Status</dt>
                    <dd className={active ? 'ok' : 'warn'}>{status || '—'}</dd>
                </div>
                <div>
                    <dt>Size</dt>
                    <dd>{sizeText(company)}</dd>
                </div>
                <div>
                    <dt>Formed</dt>
                    <dd>{formed || '—'}</dd>
                </div>
                <div>
                    <dt>Company no.</dt>
                    <dd>
                        <a href={company.url} target="_blank" rel="noreferrer" title="Open on Companies House">
                            {company.number} ↗
                        </a>
                    </dd>
                </div>
            </dl>
            {company.accounts?.turnover > 0 && (() => {
                const t = turnoverText(company.accounts);
                return (
                    <p className="ch-card-industry ch-card-turnover">
                        <span>Turnover</span>
                        <strong>{t.figure}</strong>
                        {t.change && (
                            <em className={t.pct > 0 ? 'up' : t.pct < 0 ? 'down' : undefined}>{t.change}</em>
                        )}
                        <small> (year to {t.yearTo})</small>
                    </p>
                );
            })()}
            {company.types?.length > 0 && (
                <p className="ch-card-industry">
                    <span>Business type</span>
                    {company.types.join('; ')}
                </p>
            )}
            <Directors traderId={traderId} />
        </section>
    );
};

// Only ever link out to a web address — the listing data is third-party.
const safeUrl = (value) => {
    const v = String(value || '').trim();
    if (!v) return null;
    const url = /^https?:\/\//i.test(v) ? v : `https://${v}`;
    try {
        return /^https?:$/.test(new URL(url).protocol) ? url : null;
    } catch (e) {
        return null;
    }
};

const hostOf = (url) => {
    try {
        return new URL(url).hostname.replace(/^www\./, '');
    } catch (e) {
        return url;
    }
};

// "+441773852867" -> "01773 852867"; "+442079460958" -> "020 7946 0958".
const ukPhone = (value) => {
    const m = String(value || '').replace(/[^\d+]/g, '').match(/^(?:\+44|0044|0)(\d{9,10})$/);
    if (!m) return value;
    const n = `0${m[1]}`;
    if (n.length !== 11) return `${n.slice(0, 5)} ${n.slice(5)}`;
    // 020 7946 0000 · 0117 496 0000 / 0161 496 0000 · 0300/0800 123 4567 · 01225 123456 / 07700 900000
    if (n.startsWith('02')) return `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`;
    if (/^01(1\d|\d1)/.test(n) || /^0[38]/.test(n)) return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
    return `${n.slice(0, 5)} ${n.slice(5)}`;
};

const SOCIAL_NAMES = [
    [/facebook\.com|fb\.com/, 'Facebook'], [/instagram\.com/, 'Instagram'], [/linkedin\.com/, 'LinkedIn'],
    [/twitter\.com|x\.com/, 'X'], [/youtube\.com/, 'YouTube'], [/tiktok\.com/, 'TikTok'],
];
const socialName = (url) => (SOCIAL_NAMES.find(([re]) => re.test(hostOf(url))) || [null, hostOf(url)])[1];

/* The trader's business listing on the map, when we could tie the HMRC record
 * to one (monthly match from Overture Maps: build-business-listings.js). */
const ListingCard = ({ listing }) => {
    const website = safeUrl(listing.website);
    const socials = (listing.socials || []).map(safeUrl).filter(Boolean);
    return (
        <section className="ch-card listing-card" aria-label="On the web">
            <span className="kicker">On the web</span>
            <dl className="ch-card-facts">
                <div>
                    <dt>Website</dt>
                    <dd>
                        {website ? (
                            <a href={website} target="_blank" rel="noreferrer noopener" title={listing.website_from_email ? 'Taken from their email address' : undefined}>
                                {hostOf(website)} ↗
                            </a>
                        ) : '—'}
                    </dd>
                </div>
                <div>
                    <dt>Email</dt>
                    <dd>{listing.email ? <a href={`mailto:${listing.email}`}>{listing.email}</a> : '—'}</dd>
                </div>
                <div>
                    <dt>Phone</dt>
                    <dd>{listing.phone ? <a href={`tel:${listing.phone}`}>{ukPhone(listing.phone)}</a> : '—'}</dd>
                </div>
                <div>
                    <dt>Social</dt>
                    <dd>
                        {socials.length ? socials.map((url, i) => (
                            <React.Fragment key={url}>
                                {i > 0 && ' · '}
                                <a href={url} target="_blank" rel="noreferrer noopener">{socialName(url)} ↗</a>
                            </React.Fragment>
                        )) : '—'}
                    </dd>
                </div>
            </dl>
        </section>
    );
};

/* What this trader's goods might need at the border, from the HMRC tariff
 * (monthly build: build-customs-profiles.js; rules: system/server/lib/
 * tariffFlags.js): each treatment, what it means, and the codes behind it.
 * Deliberately "might": we know the codes they trade, not where the goods
 * come from. No complexity score — dropped as ambiguous (John, 2026-10-01). */
const CODES_SHOWN = 5;
const directionText = { import: 'imports', export: 'exports', both: 'imports and exports' };

const CustomsTreatment = ({ treatment, describe }) => {
    const [open, setOpen] = useState(false);
    const control = treatmentOption(treatment.key)?.control;
    const codes = open ? treatment.codes : treatment.codes.slice(0, CODES_SHOWN);
    return (
        <li className="customs-treatment">
            <div className="customs-treatment-head">
                <span className={`customs-tag${control ? ' control' : ''}`}>{treatment.label}</span>
                <span className="customs-treatment-meaning">{treatment.meaning}</span>
            </div>
            <ul className="customs-codes">
                {codes.map((c) => (
                    <li key={c.cn8}>
                        <a href={`https://www.trade-tariff.service.gov.uk/search?q=${c.cn8}`} target="_blank" rel="noreferrer">{c.cn8}</a>
                        {describe(c.cn8) && <span className="customs-code-desc"> {describe(c.cn8)}</span>}
                        <span className="customs-code-meta">
                            {' · '}{directionText[c.direction]}, {c.months} month{c.months === 1 ? '' : 's'}
                            {c.extent === 'some' && ' · some products only'}
                        </span>
                    </li>
                ))}
            </ul>
            {treatment.codes.length > CODES_SHOWN && (
                <button type="button" className="customs-codes-toggle" onClick={() => setOpen((v) => !v)}>
                    {open ? 'Show fewer' : `Show all ${treatment.codes.length} codes`}
                </button>
            )}
        </li>
    );
};

const CustomsCard = ({ customs, trades }) => {
    // 8-digit code -> its description, from the trade rows already on the page.
    const descriptions = useMemo(() => {
        const map = new Map();
        (trades || []).forEach((t) => {
            const d = t.commodity_details;
            if (d?.Cn8Code && !map.has(d.Cn8Code)) map.set(d.Cn8Code, d.Cn8LongDescription || '');
        });
        return map;
    }, [trades]);
    const describe = (cn8) => {
        const text = descriptions.get(cn8);
        return text && text.length > 90 ? `${text.slice(0, 88)}…` : text;
    };

    return (
        <section className="ch-card customs-card" aria-label="Might need at the border">
            <span className="kicker">Might need at the border</span>
            {customs.treatments.length === 0 ? (
                <p className="customs-summary">No special border treatments found for the goods they trade.</p>
            ) : (
                <ul className="customs-treatments">
                    {customs.treatments.map((t) => (
                        <CustomsTreatment key={t.key} treatment={t} describe={describe} />
                    ))}
                </ul>
            )}
            <p className="data-attribution">
                From the HMRC UK Trade Tariff (Open Government Licence). What goods in these codes might need — the
                actual duty and checks depend on where the goods come from.
            </p>
        </section>
    );
};

const TraderDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [trader, setTrader] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaved, setIsSaved] = useState(false);
    const [savedStatus, setSavedStatus] = useState('Prospect');
    const [activeTab, setActiveTab] = useState('import-entries');
    const [hs4Filter, setHs4Filter] = useState('');

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const data = await api.get(`traders/${id}`);
                if (cancelled) return;
                setTrader(data);

                const savedStatusResp = await api.get(
                    `saved-traders/${id}/is-saved`,
                );
                if (cancelled) return;
                setIsSaved(savedStatusResp.isSaved);

                if (savedStatusResp.isSaved) {
                    const saved = await api.get(`saved-traders/${id}`);
                    if (cancelled) return;
                    setSavedStatus(saved.status);
                }
            } catch (e) {
                console.error('Error fetching trader details:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id]);

    // Returns true when the change went through (ensureSaved relies on it).
    const handleStatusChange = async (_traderId, newStatus) => {
        try {
            if (newStatus === 'Not Saved') {
                await api.delete(`saved-traders/${id}`);
                setIsSaved(false);
                setSavedStatus('Prospect');
                message.success('Removed from pipeline');
            } else if (!isSaved) {
                await api.post('saved-traders', {
                    traderId: id,
                    status: newStatus,
                });
                setIsSaved(true);
                setSavedStatus(newStatus);
                message.success('Saved to pipeline');
            } else {
                await api.put(`saved-traders/${id}/status`, { status: newStatus });
                setSavedStatus(newStatus);
                message.success('Status updated');
            }
            return true;
        } catch (e) {
            console.error('Error updating status:', e);
            // A plan limit (402) already shows the upgrade prompt.
            if (e.response?.status !== 402) message.error('Failed to update status');
            return false;
        }
    };

    // Logging a note/call/meeting on an unsaved trader saves it as a Prospect.
    const ensureSaved = async () => (isSaved ? true : handleStatusChange(id, 'Prospect'));

    // Straight to the company's own page when we matched it, else a search.
    const openCompaniesHouse = () => {
        if (!trader?.company_name) return;
        window.open(
            trader.company?.url
                || `https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(trader.company_name)}`,
            '_blank',
        );
    };

    const openGoogle = () => {
        if (!trader?.company_name) return;
        const q = trader.post_code
            ? `${trader.company_name} ${trader.post_code}`
            : trader.company_name;
        window.open(
            `https://www.google.com/search?q=${encodeURIComponent(q)}`,
            '_blank',
        );
    };

    const openLinkedIn = () => {
        if (!trader?.company_name) return;
        window.open(
            `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(trader.company_name)}`,
            '_blank',
        );
    };

    const monthly = useMemo(
        () => buildMonthlyMax(trader?.trades || [], trader?.window),
        [trader],
    );

    const direction = activeTab.startsWith('import') ? 'Import' : 'Export';
    const view = activeTab.endsWith('hs-codes') ? 'pivot' : 'entries';

    const entries = useMemo(
        () => groupEntriesByMonthHs2(trader?.trades || [], direction, hs4Filter),
        [trader, direction, hs4Filter],
    );

    const pivot = useMemo(
        () => buildPivot(trader?.trades || [], direction, hs4Filter),
        [trader, direction, hs4Filter],
    );

    // EU coverage caveat: HMRC data before Jan 2021 (exports) / Jan 2022
    // (imports) covers non-EU trade only. Invisible under today's 14-month
    // window, but becomes live the moment deeper history ships.
    const hasPreEuMonths = useMemo(
        () =>
            (trader?.trades || []).some(
                (t) =>
                    (t.trade_type === 'Export' && t.month_id < 202101) ||
                    (t.trade_type === 'Import' && t.month_id < 202201),
            ),
        [trader],
    );

    const tabCounts = useMemo(() => {
        const list = trader?.trades || [];
        const imports = list.filter((t) => t.trade_type === 'Import');
        const exports_ = list.filter((t) => t.trade_type === 'Export');
        const distinctImportHs4 = new Set(
            imports.map((t) => t.commodity_details?.Hs4Code),
        ).size;
        const distinctExportHs4 = new Set(
            exports_.map((t) => t.commodity_details?.Hs4Code),
        ).size;
        return {
            'import-entries': imports.length,
            'import-hs-codes': distinctImportHs4,
            'export-entries': exports_.length,
            'export-hs-codes': distinctExportHs4,
        };
    }, [trader]);

    if (loading) {
        return (
            <PageShell fluid>
                <Spinner block label="Loading trader" />
            </PageShell>
        );
    }

    if (!trader) {
        return (
            <PageShell fluid>
                <div className="page-head">
                    <span className="kicker">Trader</span>
                    <h1>Not found.</h1>
                    <p>
                        We couldn't find that trader.{' '}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ marginTop: 12 }}
                            onClick={() => navigate(-1)}
                        >
                            Go back
                        </button>
                    </p>
                </div>
            </PageShell>
        );
    }

    const address = [
        trader.address_1,
        trader.address_2,
        trader.address_3,
        trader.address_4,
        trader.address_5,
    ]
        .filter(Boolean)
        .join(', ');
    const displayCompanyName = toTitleCase(trader.company_name || '');
    const displayAddress = toTitleCase(address || '');

    return (
        <PageShell
            aside={
                <>
                    <ContactsPanel traderId={id} ensureSaved={ensureSaved} />
                    <ActivityPanel traderId={id} ensureSaved={ensureSaved} />
                </>
            }
        >
            <div className="trader-head">
                <button type="button" className="trader-back" onClick={() => navigate(-1)}>
                    ← Back
                </button>
                <div className="trader-title">
                    <div>
                        <h1>{displayCompanyName}</h1>
                        <div className="trader-address">
                            {displayAddress}
                            {trader.post_code ? ` · ${trader.post_code}` : ''}
                        </div>
                    </div>
                    <div className="trader-quick" data-onboard="save-trader">
                        <button type="button" className="quick-link" onClick={openCompaniesHouse}>
                            Companies House
                        </button>
                        <button type="button" className="quick-link" onClick={openGoogle}>
                            Google
                        </button>
                        <button type="button" className="quick-link" onClick={openLinkedIn}>
                            LinkedIn
                        </button>
                        <StatusSelect
                            traderId={id}
                            value={isSaved ? savedStatus : 'Not Saved'}
                            onChange={handleStatusChange}
                            placement="left"
                        />
                    </div>
                </div>
            </div>

            {trader.company && <CompaniesHouseCard company={trader.company} traderId={id} />}
            {trader.listing && <ListingCard listing={trader.listing} />}

            <div className="kpi-grid">
                <div className="kpi">
                    <span className="kpi-label">Import months</span>
                    <span className="kpi-value">{trader.import_count || 0}</span>
                    <span className="kpi-meta">Distinct months active</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Import entries</span>
                    <span className="kpi-value">
                        {(trader.import_peak_activity || 0)}
                        <span style={{ fontSize: '0.5em', fontWeight: 400, color: 'var(--muted)' }}>+</span>
                    </span>
                    <span className="kpi-meta">Customs entries (peak)</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Export months</span>
                    <span className="kpi-value">{trader.export_count || 0}</span>
                    <span className="kpi-meta">Distinct months active</span>
                </div>
                <div className="kpi">
                    <span className="kpi-label">Export entries</span>
                    <span className="kpi-value">
                        {(trader.export_peak_activity || 0)}
                        <span style={{ fontSize: '0.5em', fontWeight: 400, color: 'var(--muted)' }}>+</span>
                    </span>
                    <span className="kpi-meta">Customs entries (peak)</span>
                </div>
            </div>

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Activity</span>
                        <h2>Monthly entries.</h2>
                    </div>
                    <span className="meta">Min HS2 entries per month</span>
                </div>

                {monthly.length === 0 ? (
                    <div className="empty">
                        <p>No activity recorded.</p>
                    </div>
                ) : (
                    <div className="chart-pair">
                        <div className="chart-cell">
                            <div className="head">
                                <span className="kicker">Import</span>
                                <span className="total">
                                    {monthly.reduce((s, m) => s + m.importMax, 0)} total
                                </span>
                            </div>
                            <Bars data={monthly} valueKey="importMax" />
                        </div>
                        <div className="chart-cell">
                            <div className="head">
                                <span className="kicker">Export</span>
                                <span className="total">
                                    {monthly.reduce((s, m) => s + m.exportMax, 0)} total
                                </span>
                            </div>
                            <Bars data={monthly} valueKey="exportMax" />
                        </div>
                    </div>
                )}

                {trader?.history?.plan === 'free' && (
                    <div className="history-upgrade-strip">
                        <span>
                            Showing the last {trader.history.months} months of trade
                            data — a subscription includes 12 months of history.
                        </span>
                        <button
                            type="button"
                            onClick={() => navigate('/subscription')}
                        >
                            Subscribe →
                        </button>
                    </div>
                )}
            </section>

            {/* Below the monthly chart (John, 2026-10-01). */}
            {trader.customs && <CustomsCard customs={trader.customs} trades={trader.trades} />}

            <section className="section">
                <div className="section-head">
                    <div>
                        <span className="kicker">Trade history</span>
                        <h2>
                            {direction} {view === 'pivot' ? 'HS codes' : 'entries'}.
                        </h2>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="form-field" style={{ marginBottom: 0 }}>
                            <input
                                type="search"
                                placeholder="Filter HS4 code…"
                                value={hs4Filter}
                                onChange={(e) => setHs4Filter(e.target.value)}
                                style={{ width: 180 }}
                            />
                        </div>
                    </div>
                </div>

                <nav className="tab-bar" aria-label="Trade history">
                    {[
                        { key: 'import-entries', label: 'Import entries' },
                        { key: 'import-hs-codes', label: 'Import HS codes' },
                        { key: 'export-entries', label: 'Export entries' },
                        { key: 'export-hs-codes', label: 'Export HS codes' },
                    ].map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            className={`tab ${activeTab === t.key ? 'on' : ''}`}
                            onClick={() => setActiveTab(t.key)}
                        >
                            {t.label}
                            <span className="count">{tabCounts[t.key] || 0}</span>
                        </button>
                    ))}
                </nav>

                {view === 'entries' ? (
                    entries.length === 0 ? (
                        <div className="empty" style={{ marginTop: 18 }}>
                            <p>No entries match the current filter.</p>
                        </div>
                    ) : (
                        <div className="recent-table-wrap" style={{ marginTop: 18 }}>
                            <table className="fs-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 110 }}>Month</th>
                                        <th style={{ width: 100 }}>HS4</th>
                                        <th>Description</th>
                                        <th style={{ textAlign: 'right', width: 90 }}>Entries</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {entries.map((row, i) => {
                                        const prev = i > 0 ? entries[i - 1] : null;
                                        const showMonth =
                                            !prev || prev.month_id !== row.month_id;
                                        return (
                                            <tr key={row.trade_id}>
                                                <td className="date">
                                                    {showMonth ? formatMonthId(row.month_id) : ''}
                                                </td>
                                                <td className="num">{row.hs4_code || '—'}</td>
                                                <td>{row.hs4_description || '—'}</td>
                                                <td
                                                    className="num"
                                                    style={{ textAlign: 'right' }}
                                                >
                                                    {row.entry_count}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                ) : pivot.rows.length === 0 ? (
                    <div className="empty" style={{ marginTop: 18 }}>
                        <p>No HS codes match the current filter.</p>
                    </div>
                ) : (
                    <div className="pivot-wrap" style={{ marginTop: 18 }}>
                        <table className="fs-table pivot">
                            <thead>
                                <tr>
                                    <th className="code">HS4</th>
                                    {pivot.months.map((m) => (
                                        <th key={m}>{formatMonthId(m)}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pivot.rows.map((row) => (
                                    <tr key={row.hs4_code}>
                                        <td className="code">
                                            {row.hs4_code}
                                            <small>{row.hs4_description}</small>
                                        </td>
                                        {pivot.months.map((m) => {
                                            const v = row.months[m] || 0;
                                            return (
                                                <td
                                                    key={m}
                                                    className={v === 0 ? 'cell-zero' : 'cell-n'}
                                                >
                                                    {v === 0 ? '·' : v}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {hasPreEuMonths && (
                <p className="data-attribution">
                    Note: months before Jan 2021 (exports) / Jan 2022 (imports)
                    cover non-EU trade only.
                </p>
            )}
            {/* Open Government Licence requires attribution wherever HMRC-derived data is shown. */}
            <p className="data-attribution">
                Contains HMRC data &copy; Crown copyright
            </p>
        </PageShell>
    );
};

export default TraderDetailsPage;
